import type {
  DeliveryGroupDraft,
  DeliveryGroupStopDraft,
  LegacyStopDraft,
  RouteApplicationGroupRef
} from './deliveryGroupTypes';
import { establishMissingStopPositions } from './editorStopOrdering';

import { ensureClientExecutionUid } from './clientExecutionUid';

let groupUidSeq = 1;

export function resetDeliveryGroupUidSeq(): void {
  groupUidSeq = 1;
}

/** @deprecated test-only counter fallback; production uses UUID */
export function makeGroupUid(existing?: string | null): string {
  if (existing && String(existing).trim()) return String(existing).trim();
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `dg-${groupUidSeq++}`;
}

/** Stable execution identity — UUID, preserved across reorder/rebuild. */
export function makeStopUid(existing?: string | null): string {
  return ensureClientExecutionUid(existing);
}

export function createEmptyDeliveryGroup(params?: {
  sortOrder?: number;
  isDefault?: boolean;
  loadingPosition?: number;
  applicationId?: number | null;
  groupId?: number | null;
  label?: string | null;
}): DeliveryGroupDraft {
  const uid = makeGroupUid();
  return {
    uid,
    groupId: params?.groupId ?? null,
    applicationId: params?.applicationId ?? null,
    label: params?.label ?? null,
    sortOrder: params?.sortOrder ?? 0,
    isDefault: params?.isDefault ?? false,
    loadingPosition: params?.loadingPosition ?? 0,
    loadingStops: [],
    unloadingStops: []
  };
}

/**
 * Build delivery groups from legacy stops[] + main loading position (0).
 * Default group owns main loading (pos 0) and unloads with parentLoadingPosition 0.
 */
export function buildDeliveryGroupsFromLegacyStops(params: {
  legacyStops: LegacyStopDraft[];
  applicationGroups?: RouteApplicationGroupRef[];
}): DeliveryGroupDraft[] {
  const { legacyStops, applicationGroups } = params;
  const defaultApiGroup = applicationGroups?.find((g) => g.isDefault) ?? applicationGroups?.[0];

  const defaultGroup = createEmptyDeliveryGroup({
    sortOrder: 0,
    isDefault: true,
    loadingPosition: 0,
    groupId: defaultApiGroup?.id ?? null,
    applicationId: defaultApiGroup?.applicationId ?? null,
    label: defaultApiGroup?.label ?? 'Группа маршрута 1'
  });

  const groupsByLoadingPos = new Map<number, DeliveryGroupDraft>();
  groupsByLoadingPos.set(0, defaultGroup);

  for (const stop of legacyStops) {
    if (stop.type === 'loading') {
      const loadingPos = stop.position;
      if (loadingPos <= 0) continue;
      const apiGroup =
        applicationGroups?.find((g) => g.sortOrder === groupsByLoadingPos.size) ??
        applicationGroups?.[groupsByLoadingPos.size];
      const group = createEmptyDeliveryGroup({
        sortOrder: groupsByLoadingPos.size,
        isDefault: false,
        loadingPosition: loadingPos,
        groupId: stop.groupId ?? apiGroup?.id ?? null,
        applicationId:
          stop.pointType === 'application' && stop.pointId != null
            ? Number(stop.pointId)
            : apiGroup?.applicationId ?? null,
        label: apiGroup?.label ?? `Группа маршрута ${groupsByLoadingPos.size + 1}`
      });
      group.loadingStops.push(legacyStopToGroupStop(stop, group.uid));
      groupsByLoadingPos.set(loadingPos, group);
      continue;
    }

    if (stop.type === 'unloading') {
      const parentPos = Number(stop.parentLoadingPosition ?? 0);
      const group = groupsByLoadingPos.get(parentPos) ?? defaultGroup;
      group.unloadingStops.push(legacyStopToGroupStop(stop, group.uid));
    }
  }

  const groups = Array.from(groupsByLoadingPos.values()).sort((a, b) => a.sortOrder - b.sortOrder);
  return establishMissingStopPositions(groups);
}

function legacyStopToGroupStop(stop: LegacyStopDraft, groupUid: string): DeliveryGroupStopDraft {
  return {
    uid: stop.uid || makeStopUid(),
    id: stop.id ?? null,
    groupUid,
    type: stop.type === 'loading' ? 'loading' : 'unloading',
    position: stop.position,
    pointType: stop.pointType ?? null,
    pointId: stop.pointId ?? null,
    parentLoadingStopId: stop.parentLoadingStopId ?? null,
    parentLoadingPosition: stop.parentLoadingPosition ?? null,
    groupId: stop.groupId ?? null,
    address: stop.address ?? null,
    city: stop.city ?? null,
    contactPerson: stop.contactPerson ?? null,
    phone: stop.phone ?? null,
    plannedAt: stop.plannedAt ?? null,
    actualAt: stop.actualAt ?? null,
    notes: stop.notes ?? null,
    status: stop.status ?? null
  };
}

/** Flatten additional stops for legacy stops[] sync (main load/unload in form fields). */
export { flattenDeliveryGroupsToLegacyStops, flattenAdditionalStopsFromGroups } from './routeStopsSavePipeline';

export function computeMainUnloadingPosition(legacyStops: LegacyStopDraft[]): number {
  return legacyStops.length + 1;
}

export function refreshGroupLoadingPositions(
  groups: DeliveryGroupDraft[],
  legacyStops: LegacyStopDraft[]
): DeliveryGroupDraft[] {
  const loadingPosByGroupUid = new Map<string, number>();
  for (const s of legacyStops) {
    if (String(s.type).toLowerCase() !== 'loading') continue;
    if (!s.groupUid) continue;
    if (!loadingPosByGroupUid.has(s.groupUid)) {
      loadingPosByGroupUid.set(s.groupUid, s.position);
    }
  }
  return groups.map((g) => {
    const nextLoadingPosition = g.isDefault
      ? 0
      : loadingPosByGroupUid.get(g.uid) ?? g.loadingPosition;
    if (nextLoadingPosition === g.loadingPosition) return g;
    return { ...g, loadingPosition: nextLoadingPosition };
  });
}

export function resolveGroupApplicationIds(group: DeliveryGroupDraft): Set<number> {
  const ids = new Set<number>();
  const add = (raw: unknown) => {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) ids.add(n);
  };
  if (group.applicationId != null) add(group.applicationId);
  for (const s of [...group.loadingStops, ...group.unloadingStops]) {
    if (String(s.pointType || '').toLowerCase() === 'application') add(s.pointId);
  }
  return ids;
}
